import React, { useState } from "react";
import { Box, TextField, Button } from "@mui/material";
import styled from "styled-components";

// Wrapper to ensure consistent rendering
const SearchWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const SearchContainer = styled(Box)`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex !important;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  width: 100%;
  font-family: 'Urbanist';
  min-height: 92px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 8px;
    gap: 8px;
    flex-direction: column !important;
    align-items: stretch;
  }
`;

const StyledTextField = styled(TextField)`
  flex: 1 1 0;
  min-width: 160px;
  font-family: 'Urbanist';

  @media (max-width: 1024px) {
    min-width: 140px;
  }

  @media (max-width: 768px) {
    min-width: 120px;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: unset;
    flex: none;
  }

  & .MuiOutlinedInput-root {
    border-radius: 8px;
    height: 40px;
    background: #ffffff;
    font-family: 'Urbanist';
    width: 100%;
  }

  & .MuiInputBase-input {
    padding: 8px 16px;
    font-size: 14px;
    color: #374151;

    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }
  }

  & .MuiInputLabel-root {
    display: none;
  }

  & .MuiSelect-select {
    padding: 8px 16px;
    font-size: 14px;
    color: #374151;
    font-family: 'Urbanist';
    
    &.MuiSelect-select {
      &:empty:before {
        content: attr(data-placeholder);
        color: #9ca3af;
      }
    }
  }

  & .MuiSelect-select[aria-expanded="false"] {
    color: ${props => props.showPlaceholder ? '#9ca3af' : '#374151'};
  }
`;

const SearchButton = styled(Button)`
  background: #12188A !important;
  color: white !important;
  border-radius: 6px;
  height: 40px;
  padding: 0 24px;
  width: ${props => props.$buttonWidth || '200px'} !important;
  min-width: ${props => props.$buttonWidth || '200px'} !important;
  font-weight: 600;
  font-size: 14px;
  text-transform: none !important;
  font-family: 'Urbanist';
  white-space: nowrap;
  box-sizing: border-box;
  flex: 0 0 auto !important;
  margin-left: auto;

  @media (max-width: 768px) {
    min-width: ${props => props.$buttonWidth || '200px'} !important;
  }
  
  @media (max-width: 480px) {
    width: 100% !important;
    min-width: 100% !important;
    margin-top: 8px;
    margin-left: 0;
  }
  
  &:hover {
    background: #0d147a !important;
  }
`;

const SearchBar = ({
  onSearch,
  fields = [
    { name: "name", placeholder: "Name" },
    { name: "email", placeholder: "Email" }
  ],
  buttonText = "Search",
  buttonWidth = "200px"
}) => {
  const [filters, setFilters] = useState(
    fields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {})
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };

    setFilters(updatedFilters);
    onSearch(updatedFilters); 
  };

  const handleSearch = () => {
    onSearch(filters);
  };

  return (
    <SearchWrapper>
      <SearchContainer>
        {fields.map((field) => {
          const isSelect = field.props?.select;
          const fieldValue = filters[field.name];
          const showPlaceholder = isSelect && (!fieldValue || fieldValue === "");

          return (
            <StyledTextField
              key={field.name}
              name={field.name}
              placeholder={!isSelect ? field.placeholder : undefined}
              variant="outlined"
              value={fieldValue}
              onChange={handleChange}
              showPlaceholder={showPlaceholder}
              {...field.props}
              {...(isSelect && {
                SelectProps: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected || selected === "") {
                      return <span style={{ color: '#9ca3af' }}>{field.placeholder}</span>;
                    }
                    return selected;
                  },
                  ...field.props?.SelectProps
                }
              })}
            />
          );
        })}
        <SearchButton 
          variant="contained" 
          onClick={handleSearch}
          $buttonWidth={buttonWidth}
        >
          {buttonText}
        </SearchButton>
      </SearchContainer>
    </SearchWrapper>
  );
};

export default SearchBar;